import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useLazyGetCheckoutSessionStatusQuery } from '../slices/checkoutApiSlice';
import { clearCartItems } from '../slices/cartSlice';
import Meta from '../components/Meta';
import { rememberOrderKey } from '../utils/orderKey';
import '../assets/styles/checkout.css';

const MAX_ATTEMPTS = 20;      // ~30s of polling
const POLL_MS = 1500;

/**
 * Stripe sends shoppers here after paying (directly, or back from
 * Klarna / Clearpay / PayPal). We ask the server to confirm the payment with
 * Stripe and create the order, then hand over to the order page.
 */
const CheckoutCompleteScreen = () => {
    const [params] = useSearchParams();
    const sessionId = params.get('session_id');
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [getStatus] = useLazyGetCheckoutSessionStatusQuery();
    const [state, setState] = useState(sessionId ? 'checking' : 'missing');

    useEffect(() => {
        if (!sessionId) return undefined;
        let cancelled = false;
        let attempts = 0;
        let timer;

        const poll = async () => {
            attempts += 1;
            try {
                const res = await getStatus(sessionId).unwrap();
                if (cancelled) return;

                if (res.status === 'fulfilled' && res.orderId) {
                    dispatch(clearCartItems());
                    if (res.orderKey) rememberOrderKey(res.orderId, res.orderKey);
                    navigate(`/order/${res.orderId}?confirmed=1`, { replace: true });
                    return;
                }
                if (res.status === 'unpaid' || res.status === 'failed') {
                    setState('unpaid');
                    return;
                }
                if (res.status === 'expired') {
                    setState('expired');
                    return;
                }
            } catch {
                // network blip — keep trying
            }
            if (attempts >= MAX_ATTEMPTS) {
                setState('slow');
                return;
            }
            timer = setTimeout(poll, POLL_MS);
        };

        poll();
        return () => { cancelled = true; clearTimeout(timer); };
    }, [sessionId, getStatus, dispatch, navigate]);

    return (
        <div className="rd-checkout">
            <Meta title="Confirming your order" />
            <div className="rd-stripes" aria-hidden="true" />
            <div className="rd-confirm" role="status" aria-live="polite">
                {state === 'checking' && (
                    <>
                        <div className="rd-skel" style={{ width: 72, height: 72, borderRadius: 36 }} />
                        <h1>Confirming your payment…</h1>
                        <p>Hang tight — this only takes a moment. Please don’t refresh the page.</p>
                    </>
                )}
                {state === 'unpaid' && (
                    <>
                        <h1>Payment not completed</h1>
                        <p>Your payment didn’t go through, so you haven’t been charged. Your bag is still saved.</p>
                        <Link to="/checkout" className="rd-cta" style={{ maxWidth: 320, textDecoration: 'none' }}>Try again</Link>
                    </>
                )}
                {state === 'expired' && (
                    <>
                        <h1>Checkout timed out</h1>
                        <p>That checkout session expired before payment. You haven’t been charged.</p>
                        <Link to="/checkout" className="rd-cta" style={{ maxWidth: 320, textDecoration: 'none' }}>Start again</Link>
                    </>
                )}
                {state === 'slow' && (
                    <>
                        <h1>Still confirming…</h1>
                        <p>
                            Your payment is being processed. You’ll get a confirmation email as soon as it’s done —
                            there’s no need to pay again. Questions? <Link className="rd-link" to="/contact">Contact us</Link>.
                        </p>
                    </>
                )}
                {state === 'missing' && (
                    <>
                        <h1>Nothing to confirm</h1>
                        <p>We couldn’t find a checkout to confirm. <Link className="rd-link" to="/cart">Back to your bag</Link></p>
                    </>
                )}
            </div>
        </div>
    );
};

export default CheckoutCompleteScreen;
